/**
 * Beam System Tool - BIM 2025 Grade
 * 
 * Auto-layout of beam systems within boundaries.
 * Supports:
 * - Automatic Beam System (One-click)
 * - Sketch-based Beam System
 * - Fixed Distance / Number / Spacing rules
 * - Tagging on placement
 * - 3D Beam System orientation
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export type BeamLayoutRule =
    | 'FixedDistance'       // Justification required
    | 'FixedNumber'         // Auto calc spacing
    | 'MaximumSpacing'      // Common usage
    | 'ClearSpacing';       // Between flanges

export type BeamSystemJustification =
    | 'Beginning'
    | 'End'
    | 'Center'
    | 'DirectionLine';

export interface BeamSystemGeometryData {
    boundary: THREE.Vector3[]; // Polygon points
    beamDirection: THREE.Vector3; // Unit vector
    elevation: number; // Offset from associated level

    // Virtual Beams (generated internally)
    beams: {
        start: [number, number, number];
        end: [number, number, number];
    }[];
}

export interface BeamSystemElement extends Element {
    category: 'Beam Systems';
    geometryData: BeamSystemGeometryData;
    beamType: string; // The beam type to propagate
}

export class BeamSystemTool extends BaseTool {
    id = 'beam_system';
    name = 'Beam System';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private beamDirection: THREE.Vector3 | null = null;

    // Options
    private beamType: string = 'W12x26'; // Default
    private layoutRule: BeamLayoutRule = 'FixedDistance';
    private distanceOrCount: number = 1500; // mm or #
    private justification: BeamSystemJustification = 'Center';

    /** Sync layout options from the shared tool properties (OptionsBar). */
    activate(context: Parameters<BaseTool['activate']>[0]): void {
        super.activate(context);
        const props = context.bimStore.toolProperties;
        if (props.beamSystemRule) {
            this.layoutRule = props.beamSystemRule === 'MaximumSpacing' ? 'MaximumSpacing'
                : props.beamSystemRule === 'FixedNumber' ? 'FixedNumber'
                : 'FixedDistance';
        }
        if (this.layoutRule === 'FixedNumber') {
            this.distanceOrCount = props.beamSystemCount ?? 6;
        } else {
            this.distanceOrCount = props.beamSystemDistance ?? 1500;
        }
        if (props.beamSystemJustification) {
            this.justification = props.beamSystemJustification;
        }
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        // Basic sketch mode for now: Click points to define boundary
        const point = this.getIntersection(event);
        if (point) {
            const snapped = this.snapToGrid(point);

            // Close the loop when clicking near the first point (0.25 m hit
            // tolerance — world units are meters, so this must NOT be a mm value).
            if (this.points.length >= 3 && snapped.distanceTo(this.points[0]) < 0.25) {
                this.createBeamSystem();
                return;
            }

            this.points.push(snapped);
            this.updateSketchPreview(snapped);
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            const current = this.snapToGrid(point);
            this.updateSketchPreview(current);
        }
    }

    /**
     * Live sketch feedback via the shared 'polygon' preview type (already
     * consumed by BimViewport for floor/roof sketches).
     */
    private updateSketchPreview(current: THREE.Vector3): void {
        if (!this.context) return;
        if (this.points.length === 0) {
            this.context.appStore.setToolPreview(null);
            return;
        }
        this.context.appStore.setToolPreview({
            type: 'polygon',
            points: [...this.points, current],
            color: '#00FF00'
        });
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            if (this.points.length >= 3) {
                this.createBeamSystem();
            }
        } else if (event.key === 'Escape') {
            this.reset();
        }
    }

    private reset() {
        this.points = [];
        this.context?.appStore.setToolPreview(null);
    }

    private createBeamSystem() {
        if (!this.context || this.points.length < 3) return;

        // Auto-detect direction (longest edge by default for now)
        let maxLen = 0;
        let bestDir = new THREE.Vector3(1, 0, 0);

        for (let i = 0; i < this.points.length; i++) {
            const p1 = this.points[i];
            const p2 = this.points[(i + 1) % this.points.length];
            const d = p1.distanceTo(p2);
            if (d > maxLen) {
                maxLen = d;
                bestDir = new THREE.Vector3().subVectors(p2, p1).normalize();
            }
        }
        this.beamDirection = bestDir;

        // Generate beam centers logic (Simplified 2D logic)
        // 1. Compute bounding box aligned to direction
        // 2. Distribute lines based on rules
        // 3. Clip lines to polygon
        const beams = this.generateBeams(this.points, this.beamDirection);

        const geometryData: BeamSystemGeometryData = {
            boundary: this.points,
            beamDirection: this.beamDirection!,
            elevation: this.points[0].z,
            beams
        };

        const beamSystem: BeamSystemElement = {
            id: uuidv4(),
            category: 'Beam Systems',
            type: 'Structural Beam System',
            parameters: {
                'Beam Type': this.beamType,
                'Layout Rule': this.layoutRule,
                'Number/Spacing': this.distanceOrCount.toString(),
                'Justification': this.justification
            },
            constraints: [], // Attached to level usually
            geometryData,
            beamType: this.beamType
        };

        this.context.bimStore.addElement(beamSystem);
        this.reset();
    }

    private generateBeams(boundary: THREE.Vector3[], direction: THREE.Vector3): { start: [number, number, number], end: [number, number, number] }[] {
        const beams: { start: [number, number, number], end: [number, number, number] }[] = [];

        // Work in a 2D frame aligned to the beam direction: u = beam
        // direction, v = its perpendicular in the boundary's plane (XY is
        // assumed since sketching happens on the horizontal plane).
        const dir2 = new THREE.Vector2(direction.x, direction.y);
        if (dir2.lengthSq() < 1e-9) dir2.set(1, 0);
        dir2.normalize();
        const perp2 = new THREE.Vector2(-dir2.y, dir2.x);

        // Project the boundary onto (u, v) to find the extent to fill.
        let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
        const projected = boundary.map(p => {
            const u = p.x * dir2.x + p.y * dir2.y;
            const v = p.x * perp2.x + p.y * perp2.y;
            minU = Math.min(minU, u);
            maxU = Math.max(maxU, u);
            minV = Math.min(minV, v);
            maxV = Math.max(maxV, v);
            return { u, v };
        });

        // Layout rule: spacing along v between beam lines (m). FixedNumber
        // divides the extent into (count - 1) gaps; the mm rules are
        // converted from mm to m.
        const extentV = maxV - minV;
        let spacing: number;
        let lineCount: number;
        if (this.layoutRule === 'FixedNumber') {
            lineCount = Math.max(Math.round(this.distanceOrCount), 1);
            spacing = lineCount > 1 ? extentV / (lineCount - 1) : extentV;
        } else {
            // FixedDistance / MaximumSpacing / ClearSpacing all use a fixed
            // mm-derived spacing (MaximumSpacing caps the clear distance).
            spacing = Math.max(this.distanceOrCount / 1000, 0.1);
            lineCount = Math.floor(extentV / spacing) + 1;
        }
        const usedExtent = (lineCount - 1) * spacing;
        let startV = minV;
        if (this.justification === 'Center') startV = minV + (extentV - usedExtent) / 2;
        else if (this.justification === 'End') startV = maxV - usedExtent;

        // Beam elevation: sketch plane height (boundary Z), centered section.
        const elevation = boundary[0].z;

        // Emit one beam per layout line, clipped to the polygon via
        // scanline-style interval intersection in the (u, v) frame.
        for (let li = 0; li < lineCount; li++) {
            const v = startV + li * spacing;
            const intervals = this.computeScanlineIntervals(projected, v);
            for (const [u0, u1] of intervals) {
                const midU = (u0 + u1) / 2;
                const halfLen = (u1 - u0) / 2;
                if (halfLen < 0.05) continue; // Skip degenerate slivers

                const start = new THREE.Vector3(
                    midU * dir2.x - halfLen * dir2.x,
                    midU * dir2.y - halfLen * dir2.y,
                    elevation
                );
                const end = new THREE.Vector3(
                    midU * dir2.x + halfLen * dir2.x,
                    midU * dir2.y + halfLen * dir2.y,
                    elevation
                );
                beams.push({
                    start: start.toArray() as [number, number, number],
                    end: end.toArray() as [number, number, number]
                });
            }
        }

        return beams;
    }

    /**
     * Intersect a horizontal scanline at v with the polygon edges, returning
     * sorted [uStart, uEnd] intervals inside the boundary.
     */
    private computeScanlineIntervals(
        projected: { u: number; v: number }[],
        v: number
    ): [number, number][] {
        const xs: number[] = [];
        const n = projected.length;
        for (let i = 0; i < n; i++) {
            const a = projected[i];
            const b = projected[(i + 1) % n];
            if ((a.v <= v && b.v > v) || (b.v <= v && a.v > v)) {
                const t = (v - a.v) / (b.v - a.v);
                xs.push(a.u + t * (b.u - a.u));
            }
        }
        xs.sort((x, y) => x - y);

        const intervals: [number, number][] = [];
        for (let i = 0; i + 1 < xs.length; i += 2) {
            intervals.push([xs[i], xs[i + 1]]);
        }
        return intervals;
    }

    // Helper to snap
    onPointerUp() { }
}
